import { format } from 'date-fns';
import { MessageSquare, Clock, User } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { TimeEntryComment } from '@/services/timesheets/types';

interface TimeEntryCommentHistoryProps {
  entryId: string;
  comments: TimeEntryComment[];
  isLoading: boolean;
  isMobile: boolean;
}

export function TimeEntryCommentHistory({ 
  comments, 
  isLoading, 
  isMobile 
}: TimeEntryCommentHistoryProps) {
  if (isLoading) {
    return (
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-4 w-4" />
          <span className="font-medium text-sm">Comments</span>
        </div>
        <div className="text-sm text-muted-foreground">Loading comments...</div>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="h-4 w-4" />
          <span className="font-medium text-sm">Comments</span>
        </div>
        <div className="text-sm text-muted-foreground">No comments found</div>
      </div>
    );
  }

  const maxHeight = isMobile ? 'max-h-60' : 'max-h-40';

  return (
    <div className="p-3">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4" />
        <span className="font-medium text-sm">
          Comments ({comments.length})
        </span>
      </div>
      
      <ScrollArea className={maxHeight}>
        <div className="space-y-3">
          {comments.map((comment, index) => (
            <div key={comment.id}>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <User className="h-3 w-3" />
                  <span className="font-medium">{comment.author_name}</span>
                  <Clock className="h-3 w-3 ml-auto" />
                  <span>{format(new Date(comment.created_at), 'MMM d, yyyy h:mm a')}</span>
                </div>
                <div className="text-sm bg-muted/50 rounded p-2">
                  {comment.comment_text}
                </div>
              </div>
              {index < comments.length - 1 && <Separator className="mt-3" />}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
